import React, { useEffect, useMemo } from "react";
import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfAlertBar,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { createModal } from "../../modal";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import { useGetUsers } from "../../../services/data-list";
import {
  useUserSearchDialogState,
  SpreadsheetRecordData,
  SpreadsheetState,
  Stats,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";

const TEMPLATE_FILE_URL = "spreadsheet-template/user-search-dialog.xlsx";

export const UserSearchDialogContent: React.FC = () => {
  const { resetUserError } = useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const { moveToModalPage, closeModalPage } = usePageNavigation();
  const [getUsersResponse, sendGetUsersRequest] = useGetUsers();
  const [dialogState, setDialogState] = useUserSearchDialogState();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadsheetState>();

  const onInitialDisplayed = () => {
    moveToModalPage();
    resetUserError();
    if (dialogState.userId !== "" || dialogState.userName !== "") {
      searchUser();
    }
  };
  useEffect(onInitialDisplayed, []);

  const spreadsheetInitialData = useMemo(() => {
    if (dialogState.userList === undefined) return;

    const spreadsheetRecordData: SpreadsheetRecordData[] =
      dialogState.userList.map((user, idx) => {
        return {
          ...user,
          userRoleListStr: user.userRoleList
            .map((userRole) => {
              return `${userRole.primFlg ? "*" : ""}${userRole.roleName}:${
                userRole.cvrgCd
              }`;
            })
            .join(", "),
          idx: idx,
        };
      });

    const stats: Stats[] = dialogState.userList.map((user) => {
      return {
        isSelect: user.userId === dialogState.defaultUserId,
      };
    });

    return {
      results: spreadsheetRecordData,
      stats: stats,
    };
  }, [dialogState.userList]);

  const onChangeValueHandle = (name: string) => (event: CustomEvent) => {
    setDialogState((prevState) => ({
      ...prevState,
      [name]: event.detail.value,
    }));
  };

  const searchUser = () => {
    resetUserError();
    sendGetUsersRequest({
      userId: dialogState.userId,
      userName: dialogState.userName,
    });
  };

  const onGetUsersResponse = () => {
    if (getUsersResponse === undefined) return;

    const res = getUsersResponse?.userList ?? [];

    setDialogState((prevState) => ({
      ...prevState,
      userList: res,
      notFound: res.length > 0 ? false : true,
    }));
  };
  useEffect(onGetUsersResponse, [getUsersResponse]);

  const onCellClickedHandle = (event: CustomEvent) => {
    if (event.detail.name !== "selectFlag") {
      return;
    }

    const rowIdx: number = event.detail.cell?.data.index;
    const newSpreadsheetRecordData: SpreadsheetRecordData[] = spreadsheetState
      ? spreadsheetState.results
      : [];

    const newStats: Stats[] = spreadsheetState
      ? spreadsheetState.stats.map((row, idx) => {
          return {
            isSelect: rowIdx === idx ? true : false,
          };
        })
      : [];

    setSpreadsheetState({
      ...spreadsheetState,
      results: [...newSpreadsheetRecordData],
      stats: [...newStats],
    });

    setDialogState((prevState) => ({
      ...prevState,
      selectedUserId: newSpreadsheetRecordData[rowIdx].userId,
      selectedUserName: newSpreadsheetRecordData[rowIdx].userName,
      selectedUsermlAdrs: newSpreadsheetRecordData[rowIdx].mlAdrs,
      selectedUserRoleList: newSpreadsheetRecordData[rowIdx].userRoleList,
    }));
  };

  const onClickCancel = () => {
    closeModalPage();
    setDialogState((prevState) => ({
      ...prevState,
      isShow: false,
      isApply: false,
      notFound: false,
    }));
  };

  const onClickApply = () => {
    if (!spreadsheetState === undefined) return;

    closeModalPage();
    setDialogState((prevState) => ({
      ...prevState,
      isShow: false,
      isApply: true,
    }));
  };

  const getContent = () => {
    return (
      <>
        <div
          style={{
            margin: "auto 30px",
          }}
        >
          <FrVrfFormPanel columns={2}>
            <FrVrfButtonPanel align="left">
              <FrVrfSingleLineInputField
                labelContent="User ID (Exact Match)"
                content={dialogState.userId}
                onInputValueChanged={onChangeValueHandle("userId")}
                style={{
                  "--vrf-single-line-input-field-label-width": "120px",
                }}
              />
              <FrVrfSingleLineInputField
                labelContent="User Name (Partial Match)"
                content={dialogState.userName}
                onInputValueChanged={onChangeValueHandle("userName")}
                style={{
                  "--vrf-single-line-input-field-label-width": "170px",
                }}
              />
            </FrVrfButtonPanel>
            <FrVrfButtonPanel>
              <FrVrfButton
                content="Search"
                theme="primary"
                size="medium"
                onClicked={searchUser}
              />
            </FrVrfButtonPanel>
          </FrVrfFormPanel>
          <div style={{ paddingTop: "10px" }}>
            {dialogState.notFound && (
              <FrVrfAlertBar
                messageList={[
                  {
                    message: messageForScreen(
                      ApplicationMessageScreen.APP_SCN_00125,
                      "Specified user",
                    ),
                  },
                ]}
              />
            )}
            <FrVrfSpreadsheet
              data={spreadsheetInitialData}
              templateUrl={TEMPLATE_FILE_URL}
              onInitialized={initSpreadsheetState}
              onCellClicked={onCellClickedHandle}
              maxHeight={-80}
            />
          </div>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Apply"
              theme="primary"
              size="large"
              onClicked={onClickApply}
              disabled={!spreadsheetState?.stats.some((e) => e.isSelect)}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal("Search User", getContent(), onClickCancel, "medium");
};
